import { initials, stablefordPoints, dayHandicap } from "@/lib/scoring";

const cellClass = (strokes, par) => {
  if (!strokes) return "text-amber-700";
  const d = strokes - par;
  if (d <= -2) return "bg-[rgba(230,202,101,0.4)] rounded font-bold";
  if (d === -1) return "bg-[rgba(230,202,101,0.2)] rounded font-bold";
  if (d === 0) return "font-bold";
  if (d === 1) return "opacity-80";
  return "text-red-700 font-bold";
};

// Hole-by-hole review of the whole group's card; tap a row to jump to that hole and fix it.
export function GroupCardReview({ event, day, group, scorerId, onJumpHole, locked }) {
  const course = event.courses[day.courseKey];
  const ids = group.playerIds || [];
  const nameOf = (pid) => event.players.find((p) => p.id === pid)?.name || "?";
  const scorerInGroup = !!(scorerId && ids.includes(scorerId));
  const official = (pid, h) => group.scores?.[pid]?.[h]?.strokes ?? null;
  const shown = (pid, h) => (scorerInGroup ? group.cards?.[scorerId]?.[pid]?.[h] ?? null : official(pid, h));
  const sum = (pid, from, to) => { let t = 0; for (let h = from; h < to; h++) t += shown(pid, h) || 0; return t; };
  const pts = (pid) => {
    const player = event.players.find((p) => p.id === pid);
    let t = 0;
    for (let h = 0; h < 18; h++) { const s = shown(pid, h); if (s) t += stablefordPoints(s, course.par[h], dayHandicap(day, player), course.si[h]); }
    return t;
  };
  const mismatches = ids.reduce((n, pid) => n + Array.from({ length: 18 }, (_, h) => h).filter((h) => scorerInGroup && shown(pid, h) && official(pid, h) && shown(pid, h) !== official(pid, h)).length, 0);

  const row = (h) => (
    <tr key={h} className="tga-row border-b border-[rgba(27,51,34,0.08)] cursor-pointer active:bg-[rgba(197,160,89,0.12)]" onClick={() => !locked && onJumpHole(h)} data-testid={`card-review-row-${h}`}>
      <td className="py-1.5 pl-1 font-bold text-[color:var(--brass-dark)]">{h + 1}</td>
      <td className="py-1.5 text-center opacity-60">{course.par[h]}</td>
      {ids.map((pid) => {
        const v = shown(pid, h);
        const diff = scorerInGroup && v && official(pid, h) && v !== official(pid, h);
        return (
          <td key={pid} className="py-1.5 text-center">
            <span className={`inline-block min-w-[28px] px-1 ${cellClass(v, course.par[h])} ${diff ? "ring-2 ring-red-500 rounded" : ""}`} title={diff ? `Official card says ${official(pid, h)}` : undefined}>{v || "–"}</span>
          </td>
        );
      })}
    </tr>
  );
  const totalRow = (label, from, to, testId) => (
    <tr className="border-t border-[rgba(27,51,34,0.2)] bg-[rgba(27,51,34,0.04)]" data-testid={testId}>
      <td className="py-1.5 pl-1 text-[10px] uppercase text-[color:var(--brass-dark)]" colSpan={2}>{label}</td>
      {ids.map((pid) => <td key={pid} className="py-1.5 text-center font-bold">{sum(pid, from, to) || "–"}</td>)}
    </tr>
  );

  return (
    <div className="tga-card p-3" data-testid="card-review">
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs uppercase tracking-wide text-[color:var(--brass-dark)]">Check every hole</div>
        <div className="text-[11px] text-[color:var(--text-dark)]/60">{locked ? "Card locked" : "Tap a hole to fix it"}</div>
      </div>
      {scorerInGroup && <p className="helptext text-[color:var(--text-dark)]/60 mb-2">Showing <b>your</b> card.{mismatches > 0 ? ` ${mismatches} score${mismatches === 1 ? "" : "s"} outlined in red differ from the official card.` : ""}</p>}
      <table className="w-full text-sm font-mono-metrics">
        <thead>
          <tr className="text-[10px] uppercase text-[color:var(--brass-dark)] border-b border-[rgba(27,51,34,0.15)]">
            <th className="py-1 pl-1 text-left">Hole</th>
            <th className="py-1 text-center">Par</th>
            {ids.map((pid) => <th key={pid} className="py-1 text-center" title={nameOf(pid)}><span className="avatar inline-flex" style={{ width: 22, height: 22, fontSize: 10 }}>{initials(nameOf(pid))}</span></th>)}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 9 }, (_, h) => row(h))}
          {totalRow("Out", 0, 9, "card-review-out")}
          {Array.from({ length: 9 }, (_, i) => row(i + 9))}
          {totalRow("In", 9, 18, "card-review-in")}
          {totalRow("Total", 0, 18, "card-review-total")}
          <tr className="bg-[rgba(197,160,89,0.15)]" data-testid="card-review-points">
            <td className="py-1.5 pl-1 text-[10px] uppercase text-[color:var(--brass-dark)]" colSpan={2}>Points</td>
            {ids.map((pid) => <td key={pid} className="py-1.5 text-center font-bold">{pts(pid)}</td>)}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
